/**
 * LandingPage — Öffentliches Schaufenster (Akquise), Premium-/Cinematic-Ausbaustufe.
 *
 * Wird in App.js für nicht angemeldete Besucher auf "/" gerendert (vor dem Auth-Gate).
 * Rein statisch — keine API-Aufrufe, kein Tenant-Bezug. Sicher öffentlich.
 * Cinematic-Layer ist reines CSS/SVG (index.css); Produkt-Screens sind echte App-Screenshots.
 */
import { Link } from "react-router-dom";
import { useKanalVerfuegbarkeit, KANAL_FALLBACK } from "../hooks/useKanalVerfuegbarkeit";
import {
  Sparkles, ShieldCheck, Server, CreditCard, Send,
  CheckCircle, ArrowRight, Lock, Zap, Clock, Layers,
} from "lucide-react";
import { pricingPlans, trialConfig } from "../data/pricing";
import { company } from "../data/company";
import { trackEvent } from "../lib/trackEvent";

const REGISTER = "/login?tab=register";

const STATS = [
  { icon: Layers, value: "8", label: "Plattformen geplant — heute Bluesky & LinkedIn live" },
  { icon: Zap, value: "1 Klick", label: "= ein ganzer Monat Content" },
  { icon: Clock, value: "~50 Sek.", label: "bis zum fertigen Plan" },
  { icon: CreditCard, value: `${trialConfig.days} Tage`, label: "kostenlos, ohne Kreditkarte" },
];

const BENEFITS = [
  { icon: Sparkles, title: "KI-Monatsplan auf einen Klick", text: "Die KI erstellt einen kompletten Monat an Beiträgen — passend zu deiner Marke, deiner Zielgruppe und deinen Themen. Ohne leeres Blatt." },
  { icon: Send, title: "Plant & veröffentlicht für dich", text: "Beiträge werden geschrieben, terminiert und zur richtigen Zeit auf deinen verbundenen Kanälen veröffentlicht — du entscheidest, welche Netzwerke du anbindest." },
  { icon: ShieldCheck, title: "DSGVO-nativ, ohne Tech-Wissen", text: "Gemacht für Selbstständige, Unternehmen und Teams in Deutschland: datenschutzkonform, in der EU gehostet, ohne KI-Trainingsnutzung deiner Inhalte." },
];

const STEPS = [
  { n: "1", title: "Markenprofil in 2 Minuten", text: "Kurz dein Angebot, deine Zielgruppe und deinen Ton festlegen — oder automatisch aus deiner Website ableiten lassen." },
  { n: "2", title: "Monatsplan generieren", text: "Ein Klick — die KI baut deinen kompletten Content-Monat mit Beiträgen, Hooks und Hashtags." },
  { n: "3", title: "Kanal verbinden & live gehen", text: "Bluesky verbindest du in 2 Minuten selbst — dort veröffentlicht Förderfuchs direkt. Facebook & Instagram sind bei Meta in der Freischaltung; bis dahin bekommst du jeden Beitrag fertig formatiert zum Kopieren & Einplanen." },
];

const AUDIENCE = ["Selbstständige & Freelancer", "Lokale Unternehmen", "Handwerk & Praxen", "Teams & Organisationen", "Coaches & Berater"];
const TRUST = [
  { icon: ShieldCheck, label: "DSGVO-konform" },
  { icon: Server, label: "Daten in der EU" },
  { icon: Lock, label: "Keine KI-Trainingsnutzung" },
  { icon: CreditCard, label: "Ohne Kreditkarte · monatlich kündbar" },
];

function PrimaryCta({ position, children, dark }) {
  return (
    <Link
      to={REGISTER}
      onClick={() => trackEvent("landing_cta_clicked", { cta_position: position })}
      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-fox-fire text-white rounded-xl font-bold hover:bg-fox-fire-dark transition-colors btn-glow"
    >
      {children} <ArrowRight size={18} />
    </Link>
  );
}

export default function LandingPage() {
  const { satz: kanalSatz } = useKanalVerfuegbarkeit();
  return (
    <div className="min-h-screen bg-stone-50 text-stone-800">
      {/* ═══ Cinematic Hero ═══════════════════════════════════════════════════ */}
      <section className="cinematic">
        <div className="orb orb-fire"   style={{ width: 420, height: 420, top: -110, left: -80 }} />
        <div className="orb orb-indigo" style={{ width: 460, height: 460, top: 20, right: -120 }} />
        <div className="orb orb-gold"   style={{ width: 320, height: 320, bottom: -120, left: "42%" }} />

        {/* Nav (auf dunklem Grund) */}
        <header className="relative z-10">
          <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <img src="/images/logo.png" alt="Förderfuchs" width="240" height="276" className="h-9 w-auto drop-shadow" />
              <span className="flex flex-col leading-tight">
                <span className="font-heading font-extrabold text-[15px] text-white">KI Social Media Manager</span>
                <span className="text-[10px] text-white/50 font-medium tracking-wide">by Förderfuchs</span>
              </span>
            </div>
            <nav className="flex items-center gap-2 sm:gap-4 text-sm">
              <Link to="/pricing" className="hidden sm:inline text-white/70 hover:text-white transition-colors">Preise</Link>
              <Link to="/vertrauen" className="hidden sm:inline text-white/70 hover:text-white transition-colors">Vertrauen &amp; DSGVO</Link>
              <Link to="/login?tab=login" className="text-white/70 hover:text-white transition-colors">Anmelden</Link>
              <Link
                to={REGISTER}
                onClick={() => trackEvent("landing_cta_clicked", { cta_position: "nav" })}
                className="px-4 py-2 bg-fox-fire text-white rounded-lg font-semibold hover:bg-fox-fire-dark transition-colors btn-glow"
              >
                Kostenlos starten
              </Link>
            </nav>
          </div>
        </header>

        {/* Hero-Text */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 pt-14 pb-4 text-center reveal">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/15 backdrop-blur rounded-full text-xs text-white/85 font-medium mb-6">
            <Sparkles size={12} className="text-brand-gold" /> Für alle, die keine Zeit für Social Media haben — DACH-Region
          </div>
          <h1 className="font-heading font-extrabold text-4xl md:text-6xl leading-[1.05] tracking-tight text-white">
            Ein ganzer Monat Social&nbsp;Media —{" "}<br className="hidden md:block" />
            <span className="text-cinematic">auf einen Klick.</span>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto mt-6 leading-relaxed">
            Die KI erstellt deinen Monatsplan, schreibt die Beiträge und veröffentlicht sie auf
            deinen verbundenen Kanälen. Ohne Tech-Wissen. {trialConfig.days} Tage kostenlos, ohne Kreditkarte.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <PrimaryCta position="hero">{trialConfig.days} Tage kostenlos starten</PrimaryCta>
            <Link to="/pricing" className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/15 text-white rounded-xl font-semibold hover:bg-white/15 transition-colors backdrop-blur">
              Preise ansehen
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-white/55 mt-7">
            {TRUST.map((t) => (
              <span key={t.label} className="inline-flex items-center gap-1.5">
                <t.icon size={14} className="text-brand-gold" /> {t.label}
              </span>
            ))}
          </div>
        </div>

        {/* Echter Produkt-Screen im Device-Frame */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 pt-10 pb-20 reveal" style={{ animationDelay: ".12s" }}>
          <div className="device-frame">
            <div className="device-bar">
              <span className="device-dot" style={{ background: "#ff5f57" }} />
              <span className="device-dot" style={{ background: "#febc2e" }} />
              <span className="device-dot" style={{ background: "#28c840" }} />
              <span className="ml-3 text-[11px] text-white/40 truncate">socialmedia.foerderfuchs-bildung.de</span>
            </div>
            <img src="/images/screens/screen-kanaele.jpg"
              alt="Produktansicht: die Kanal-Übersicht im KI Social Media Manager"
              width="1280" height="800" className="w-full block" loading="lazy" />
          </div>
          {/* ── Ehrliche Bildunterschrift (Befund 19.08.2026, §5 UWG) ──────────
              Der Screenshot oben stammt vom 04.07. und zeigt FÜNF orangefarbene
              „Verbinden"-Knöpfe (Facebook, LinkedIn, TikTok, Google Business,
              Threads). Verbinden kann ein Kunde davon heute genau einen. Bluesky —
              der einzige Kanal, der ohne Provider-Freigabe immer funktioniert —
              kommt im Bild gar nicht vor.
              Die Aussage steht deshalb direkt darunter im Lesefluss und kommt aus
              derselben Quelle wie der Verbinden-Riegel: Sie wächst von selbst, sobald
              Meta freigibt, und kann nicht wieder veralten wie ein Bild. */}
          <p className="text-xs text-white/55 text-center mt-3 max-w-2xl mx-auto leading-relaxed">
            {kanalSatz || KANAL_FALLBACK}{" "}
            Die übrigen Kanäle sind noch in der Freigabe durch den jeweiligen Anbieter —
            bis dahin bekommst du jeden Beitrag fertig formatiert zum Kopieren.
          </p>
        </div>
      </section>

      {/* ═══ Stat-/Proof-Band (überlappt den Hero) ═══════════════════════════ */}
      <section className="max-w-5xl mx-auto px-4 relative z-20 -mt-10">
        <div className="premium-card grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-stone-100 overflow-hidden">
          {STATS.map((s) => (
            <div key={s.label} className="p-5 text-center">
              <s.icon size={18} className="text-fox-fire mx-auto mb-2" />
              <div className="font-heading font-extrabold text-2xl text-stone-900 leading-none">{s.value}</div>
              <div className="text-xs text-stone-500 mt-1.5 leading-snug">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ Nutzen ═══════════════════════════════════════════════════════════ */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {BENEFITS.map((b) => (
            <div key={b.title} className="premium-card p-6">
              <div className="w-11 h-11 bg-fox-tint rounded-xl flex items-center justify-center mb-4 ring-1 ring-fox-fire/10">
                <b.icon size={22} className="text-fox-fire" />
              </div>
              <h3 className="font-heading font-bold text-lg text-stone-900 mb-2">{b.title}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 mt-10">
          {AUDIENCE.map((a) => (
            <span key={a} className="text-xs px-3 py-1.5 rounded-full bg-white border border-stone-200 text-stone-600">{a}</span>
          ))}
        </div>
      </section>

      {/* ═══ So funktioniert's ════════════════════════════════════════════════ */}
      <section id="so-gehts" className="max-w-6xl mx-auto px-4 pb-16">
        <h2 className="font-heading font-bold text-2xl md:text-3xl text-center text-stone-900 mb-2">In 3 Schritten startklar</h2>
        <p className="text-stone-500 text-center mb-10">Kein Redaktionsplan, kein leeres Blatt, kein Tool-Dschungel.</p>
        <div className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className="premium-card p-6">
              <div className="w-9 h-9 brand-gradient text-white rounded-full flex items-center justify-center font-bold mb-4 shadow-sm shadow-orange-200">{s.n}</div>
              <h3 className="font-heading font-bold text-lg text-stone-900 mb-2">{s.title}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ Trust-Block (cinematic) ══════════════════════════════════════════ */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="cinematic rounded-3xl p-8 md:p-12">
          <div className="orb orb-indigo" style={{ width: 320, height: 320, top: -60, right: -40 }} />
          <div className="relative z-10 max-w-2xl">
            <ShieldCheck size={30} className="text-brand-gold mb-4" />
            <h2 className="font-heading font-bold text-2xl md:text-3xl mb-3 text-white">Deine Daten. Deine Kontrolle.</h2>
            <p className="text-white/70 leading-relaxed mb-6">
              Datenschutz ist bei einer Zielgruppe mit Teilnehmer- und Klientendaten kein Nachgedanke.
              Förderfuchs ist DSGVO-nativ gebaut: gehostet in der EU, eigener isolierter Arbeitsbereich
              je Kunde, KI-Anbieter offen ausgewiesen — und deine Inhalte werden nicht zum Training der KI-Modelle verwendet.
            </p>
            <Link to="/vertrauen" className="inline-flex items-center gap-2 text-brand-gold font-semibold hover:text-white transition-colors">
              Mehr zu Vertrauen &amp; DSGVO <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ Pricing-Teaser (Premium-Karten) ══════════════════════════════════ */}
      <section className="max-w-5xl mx-auto px-4 pb-16">
        <h2 className="font-heading font-bold text-2xl md:text-3xl text-center text-stone-900 mb-2">Einfache Preise, monatlich kündbar</h2>
        <p className="text-stone-500 text-center mb-2">{trialConfig.days} Tage kostenlos testen — ohne Kreditkarte.</p>
        <p className="text-stone-400 text-center text-sm mb-10">{trialConfig.channelNote}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {pricingPlans.filter((p) => !p.hidden).map((p) => (
            <div key={p.id} className={`premium-card p-6 ${p.highlighted ? "premium-card-glow" : ""}`}>
              {p.badge && <span className="text-[10px] font-bold uppercase tracking-wider text-fox-fire">{p.badge}</span>}
              <h3 className="font-heading font-bold text-lg text-stone-900">{p.name}</h3>
              <div className="my-2">
                <span className="text-3xl font-extrabold text-stone-900">{p.priceLabel || `${p.priceMonthly} €`}</span>
                {!p.priceLabel && <span className="text-sm text-stone-500"> / Monat</span>}
              </div>
              <p className="text-xs text-stone-500 leading-relaxed mb-4">{p.description}</p>
              <ul className="space-y-1.5">
                {p.idealFor.slice(0, 3).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-stone-600">
                    <CheckCircle size={14} className="text-fox-fire flex-shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/pricing" className="inline-flex items-center gap-2 text-fox-fire font-semibold hover:underline">
            Alle Pakete &amp; Funktionen vergleichen <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* ═══ Abschluss-CTA (cinematic) ════════════════════════════════════════ */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="cinematic rounded-3xl px-6 py-14 text-center">
          <div className="orb orb-fire" style={{ width: 300, height: 300, top: -60, left: "30%" }} />
          <div className="relative z-10">
            <h2 className="font-heading font-extrabold text-2xl md:text-4xl text-white mb-3">
              Heute starten — dein erster <span className="text-cinematic">Monatsplan</span> steht in 2 Minuten.
            </h2>
            <p className="text-white/60 mb-8 max-w-xl mx-auto">{trialConfig.days} Tage kostenlos, ohne Kreditkarte. {trialConfig.channelNote} {trialConfig.disclaimer}</p>
            <PrimaryCta position="footer">{trialConfig.days} Tage kostenlos starten</PrimaryCta>
          </div>
        </div>
      </section>

      {/* ═══ Footer ═══════════════════════════════════════════════════════════ */}
      <footer className="border-t border-stone-100">
        <div className="max-w-6xl mx-auto px-4 py-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-stone-400">
          <span>© {new Date().getFullYear()} {company.legalName}</span>
          <Link to="/impressum" className="hover:text-stone-600">Impressum</Link>
          <Link to="/datenschutz" className="hover:text-stone-600">Datenschutz</Link>
          <Link to="/vertrauen" className="hover:text-stone-600">Vertrauen &amp; DSGVO</Link>
          <Link to="/nutzungsbedingungen" className="hover:text-stone-600">AGB</Link>
          <Link to="/pricing" className="hover:text-stone-600">Preise</Link>
        </div>
      </footer>
    </div>
  );
}
